import {
  buildRevisionDiffDetailViewModel,
  diffTouchesMember,
  resolvePairDetail,
  type RevisionDetailEventRow,
  type RevisionDiffDetailViewModel,
  selectDefaultDetail,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import type { CanonicalRevisionModel } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionMemberCanonical'
import {
  type FimRevisionDiffDetailEntry,
  type RevisionDiffDetailBox,
  type RevisionObjectStatus,
  useRevisionDiffDetailFile,
} from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import { resolveDerivedPageImageFileRef } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import type { PageRevisionFamilyMember } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { useRolodexSignedURLs } from 'features/rolodex/hooks/useRolodexSignedURLs'
import { useCallback, useEffect, useMemo, useState } from 'react'

type UseRevisionCompareSessionArgs = {
  /** Whether the compare dialog is open. Gates the from/to image fetches. */
  open: boolean
  /** Detail entries touching the current document, fetched by the caller. */
  details: readonly FimRevisionDiffDetailEntry[]
  canonicalRevisionModel: CanonicalRevisionModel
  currentBqDocumentId: string
  currentPageNum: number
  /** Pre-selects this detail entry when it touches the current page. */
  initialDetailId?: string
}

/** Pane header fields for one side of the compared pair. */
export type RevisionSideDisplayMeta = {
  revision: string | null
  documentTitle: string | null
  revisionDate: string | null
}

type UseRevisionCompareSessionResult = {
  hasSelectedDetail: boolean
  /** Keys the panes so a different detail resets their pan/zoom. */
  selectedDetailId: string | undefined
  showOverlay: boolean
  setShowOverlay: (showOverlay: boolean) => void
  fromImageUrl: string | undefined
  isLoadingFromImage: boolean
  toImageUrl: string | undefined
  isLoadingToImage: boolean
  fromMeta: RevisionSideDisplayMeta
  toMeta: RevisionSideDisplayMeta
  /** Present once the selected detail's file has loaded. */
  detailViewModel: RevisionDiffDetailViewModel | undefined
  isLoadingDetailFile: boolean
  selectedEventId: string | undefined
  selectedObjectIds: ReadonlySet<string>
  /** Boxes to highlight for a selected event that has no object shapes. */
  regionFrom: RevisionDiffDetailBox | null
  regionTo: RevisionDiffDetailBox | null
  selectObject: (objectId: string) => void
  selectEvent: (event: RevisionDetailEventRow) => void
  hiddenStatuses: ReadonlySet<RevisionObjectStatus>
  toggleStatus: (status: RevisionObjectStatus) => void
  hiddenImpacts: ReadonlySet<string>
  toggleImpact: (impact: string) => void
  /** Switches to the generated diff for one canonical from/to pair. */
  selectPair: (
    from: PageRevisionFamilyMember,
    to: PageRevisionFamilyMember
  ) => void
  fromDocumentId: string | undefined
  toDocumentId: string | undefined
  /** One member per revision (latest-first), labelled with display values. */
  canonicalMembers: readonly PageRevisionFamilyMember[]
  /** `canonicalMembers` minus revisions the user edited out of the family. */
  familyMembers: readonly PageRevisionFamilyMember[]
}

/** Matched objects can be numerous, so they start hidden. */
const DEFAULT_HIDDEN_STATUSES: ReadonlySet<RevisionObjectStatus> = new Set([
  'matched',
])

function resolveSideMeta(
  members: readonly PageRevisionFamilyMember[],
  bqDocumentId: string | undefined,
  pageNum: number | undefined,
  revision: string | null | undefined
): RevisionSideDisplayMeta {
  const member = members.find(
    (candidate) =>
      candidate.bqDocumentId === bqDocumentId && candidate.pageNum === pageNum
  )
  return {
    revision: revision ?? null,
    documentTitle:
      member?.systemPage.entry.data.document_title ?? member?.filename ?? null,
    revisionDate: member?.revisionDate ?? null,
  }
}

/**
 * Owns the state and derived data for one open `RevisionCompareView` session:
 * which detail entry is selected (defaulting to a page-scoped pick), its lazily
 * loaded view model, and the from/to page image fetches. The file and image
 * fetches are gated on `open` so no requests are made while the dialog is
 * closed (the view is mounted persistently by its caller).
 */
export function useRevisionCompareSession({
  open,
  details,
  canonicalRevisionModel,
  currentBqDocumentId,
  currentPageNum,
  initialDetailId,
}: UseRevisionCompareSessionArgs): UseRevisionCompareSessionResult {
  const { canonicalMembers, canonicalEndpoint, familyMembers } =
    canonicalRevisionModel
  const { bqDocumentId: currentCanonicalId, pageNum: currentCanonicalPage } =
    canonicalEndpoint(currentBqDocumentId, currentPageNum)

  const pageScopedDetails = useMemo(
    () =>
      details.filter((detail) =>
        diffTouchesMember(
          detail.data,
          currentCanonicalId,
          currentCanonicalPage,
          canonicalEndpoint
        )
      ),
    [details, currentCanonicalId, currentCanonicalPage, canonicalEndpoint]
  )

  const defaultDetailId = useMemo(
    () =>
      selectDefaultDetail(
        details,
        familyMembers,
        currentCanonicalId,
        currentCanonicalPage,
        canonicalEndpoint
      ),
    [
      details,
      familyMembers,
      currentCanonicalId,
      currentCanonicalPage,
      canonicalEndpoint,
    ]
  )

  const pinnedDetail = initialDetailId
    ? details.find((detail) => detail.entryId === initialDetailId)
    : undefined
  const [pickedDetailId, setPickedDetailId] = useState<string | undefined>(
    undefined
  )
  const pickedDetail = pickedDetailId
    ? details.find((detail) => detail.entryId === pickedDetailId)
    : undefined
  const selectedDetail =
    pickedDetail ??
    pinnedDetail ??
    pageScopedDetails.find((detail) => detail.entryId === defaultDetailId)

  useEffect(() => {
    if (!open) setPickedDetailId(undefined)
  }, [open])
  useEffect(() => {
    setPickedDetailId(undefined)
  }, [currentBqDocumentId, currentPageNum, initialDetailId])

  const selectPair = useCallback(
    (from: PageRevisionFamilyMember, to: PageRevisionFamilyMember) => {
      const pair = resolvePairDetail(details, from, to, canonicalEndpoint)
      if (pair) setPickedDetailId(pair.entryId)
    },
    [details, canonicalEndpoint]
  )

  const [showOverlay, setShowOverlay] = useState(true)

  const { detailsFile, isLoading: isLoadingDetailFile } =
    useRevisionDiffDetailFile(selectedDetail, { enabled: open })

  const [selectedEventId, setSelectedEventId] = useState<string>()
  const [selectedObjectIds, setSelectedObjectIds] = useState<
    ReadonlySet<string>
  >(new Set())
  const selectedDetailId = selectedDetail?.entryId

  useEffect(() => {
    setSelectedEventId(undefined)
    setSelectedObjectIds(new Set())
  }, [selectedDetailId])

  const [hiddenStatuses, setHiddenStatuses] = useState(DEFAULT_HIDDEN_STATUSES)
  const toggleStatus = useCallback((status: RevisionObjectStatus) => {
    setHiddenStatuses((previous) => {
      const next = new Set(previous)
      if (next.has(status)) {
        next.delete(status)
      } else {
        next.add(status)
      }
      return next
    })
  }, [])

  const [hiddenImpacts, setHiddenImpacts] = useState<ReadonlySet<string>>(
    new Set()
  )
  const toggleImpact = useCallback((impact: string) => {
    setHiddenImpacts((previous) => {
      const next = new Set(previous)
      if (next.has(impact)) {
        next.delete(impact)
      } else {
        next.add(impact)
      }
      return next
    })
  }, [])

  const detailViewModel = useMemo(
    () =>
      selectedDetail && detailsFile
        ? buildRevisionDiffDetailViewModel(
            selectedDetail.data,
            detailsFile,
            hiddenStatuses,
            hiddenImpacts
          )
        : undefined,
    [selectedDetail, detailsFile, hiddenStatuses, hiddenImpacts]
  )

  const selectObject = useCallback(
    (objectId: string) => {
      setSelectedObjectIds(new Set([objectId]))
      setSelectedEventId(detailViewModel?.eventIdByObjectId.get(objectId))
    },
    [detailViewModel]
  )
  const selectEvent = useCallback((event: RevisionDetailEventRow) => {
    setSelectedEventId(event.id)
    setSelectedObjectIds(new Set(event.objectRefs))
  }, [])

  const selectedEvent = useMemo(() => {
    if (!detailViewModel || !selectedEventId) return undefined
    const all = [
      ...detailViewModel.sections.flatMap((section) => section.events),
      ...detailViewModel.unsectionedEvents,
    ]
    return all.find((event) => event.id === selectedEventId)
  }, [detailViewModel, selectedEventId])
  const isRegionOnly = selectedEvent?.objectRefs.length === 0
  const regionFrom = isRegionOnly ? (selectedEvent?.fromBox ?? null) : null
  const regionTo = isRegionOnly ? (selectedEvent?.toBox ?? null) : null

  const fromImageRef = useMemo(
    () =>
      open && selectedDetail
        ? resolveDerivedPageImageFileRef({
            siteId: selectedDetail.data.site_id,
            bqDocumentId: selectedDetail.data.from_document_id,
            pageNumber: selectedDetail.data.from_page_num,
            variant: 'hiRes',
          })
        : undefined,
    [open, selectedDetail]
  )
  const toImageRef = useMemo(
    () =>
      open && selectedDetail
        ? resolveDerivedPageImageFileRef({
            siteId: selectedDetail.data.site_id,
            bqDocumentId: selectedDetail.data.to_document_id,
            pageNumber: selectedDetail.data.to_page_num,
            variant: 'hiRes',
          })
        : undefined,
    [open, selectedDetail]
  )
  const { url: fromImageUrl, isLoading: isLoadingFromImage } =
    useRolodexSignedURLs(fromImageRef)
  const { url: toImageUrl, isLoading: isLoadingToImage } =
    useRolodexSignedURLs(toImageRef)

  const selectedFrom = selectedDetail
    ? canonicalEndpoint(
        selectedDetail.data.from_document_id,
        selectedDetail.data.from_page_num
      )
    : undefined
  const selectedTo = selectedDetail
    ? canonicalEndpoint(
        selectedDetail.data.to_document_id,
        selectedDetail.data.to_page_num
      )
    : undefined

  const fromMeta = resolveSideMeta(
    canonicalMembers,
    selectedFrom?.bqDocumentId,
    selectedFrom?.pageNum,
    selectedDetail?.data.from_revision
  )
  const toMeta = resolveSideMeta(
    canonicalMembers,
    selectedTo?.bqDocumentId,
    selectedTo?.pageNum,
    selectedDetail?.data.to_revision
  )

  return {
    hasSelectedDetail: selectedDetail !== undefined,
    selectedDetailId,
    showOverlay,
    setShowOverlay,
    fromImageUrl,
    isLoadingFromImage,
    toImageUrl,
    isLoadingToImage,
    fromMeta,
    toMeta,
    detailViewModel,
    isLoadingDetailFile,
    selectedEventId,
    selectedObjectIds,
    regionFrom,
    regionTo,
    selectObject,
    selectEvent,
    hiddenStatuses,
    toggleStatus,
    hiddenImpacts,
    toggleImpact,
    selectPair,
    fromDocumentId: selectedFrom?.bqDocumentId,
    toDocumentId: selectedTo?.bqDocumentId,
    canonicalMembers,
    familyMembers,
  }
}
